import { SaleDocumentType } from '__generated__/globalTypes';

import { CONFIDENTIALITY_AGREEMENT_TEXT } from './confidentialityAgreementText';

// The Special PII Notice is not one of these: it is shown before every Level 2 open, never
// acknowledged once (features/irm/piiNotice.ts).
export type ClickThroughDocumentType = Exclude<SaleDocumentType, SaleDocumentType.PII_NOTICE>;

export interface ClickThroughDocument {
  doc_type: ClickThroughDocumentType;
  title: string;
  version: string;
  text: string;
  // api sends it with the documents it serves; the client hashes the CA itself.
  document_sha256?: string;
}

// The CA is an unmarked bidder-facing form, so the bundle carries it. The PEC summary and the
// summary bid notice come from Exhibit 6, which is marked CONTROLLED//FDIC BUSINESS: api serves
// them to signed-in callers only (saleAcknowledgementDocument), with their versions.
// loan-management's seedDemoSale acknowledges this CA version and hash for its demo bidders.
export const CONFIDENTIALITY_AGREEMENT: ClickThroughDocument = {
  doc_type: SaleDocumentType.CA,
  title: 'Confidentiality Agreement',
  version: 'fdic-ca-v7-2022-12-07',
  text: CONFIDENTIALITY_AGREEMENT_TEXT,
};

// acknowledgements holds every version a caller has ever accepted -- matching on doc_type alone
// would treat an old version as still current. A document's text changes only with its version,
// so everyone who clicked through the old text is shown the new one.
export function isDocumentAcknowledged(
  acknowledgements: { doc_type: SaleDocumentType; version: string }[],
  document: Pick<ClickThroughDocument, 'doc_type' | 'version'>,
): boolean {
  return acknowledgements.some(
    (ack) => ack.doc_type === document.doc_type && ack.version === document.version,
  );
}

// loan-service's own rule (CurrentRow): the CA at its current version, the PEC summary at any
// version. After the deadline loan-service refuses new acknowledgements, so a stricter rule would
// strand a user whose only PEC summary predates a version bump.
export function hasLoanServiceAccessDocuments(
  acknowledgements: { doc_type: SaleDocumentType; version: string }[],
): boolean {
  return (
    isDocumentAcknowledged(acknowledgements, CONFIDENTIALITY_AGREEMENT) &&
    acknowledgements.some((ack) => ack.doc_type === SaleDocumentType.PEC_SUMMARY)
  );
}

export async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
